import ReactHtmlParser from "react-html-parser";
import { FormattedMessage } from "react-intl";
import { OverlayTrigger, Tooltip } from "react-bootstrap";

import { ReactComponent as InfoCircleIcon } from "assets/images/iconsV2/info-circle.svg";
import Avatar from "components/v2/Avatar/Avatar";
import { useGetCostRecoverySetting } from "api/queries/location/useGetCostRecoverySetting";
import getLabel, { getLabelWithTranslationFallback } from "helpers/getLabel";
import { replaceLinebreaks } from "helpers/commonHelpers";
import { replaceLinks } from "helpers/urlHelpers";
import { DateTimeFormat, formatDate } from "helpers/dateTimeHelper";

import { DetailsSheetSection, SheetField } from "../DetailsSheetSection";
import { SHIPMENT_DETAILS_TEST_IDS } from "../testIds";
import type { ShipmentDetailsSheetViewModel, UserType } from "../types";

type OrganisationSectionProps = {
  organisation: ShipmentDetailsSheetViewModel["organisation"];
  userType: UserType;
  paymentLocationReference?: string;
  isAutomatedBooking?: boolean;
};

export function OrganisationSection({
  organisation,
  userType,
  paymentLocationReference,
  isAutomatedBooking = false,
}: OrganisationSectionProps) {
  const { hideInPrint, fields } = organisation;
  const isProvider = userType === "provider";

  const { data: costRecoverySetting } = useGetCostRecoverySetting(
    paymentLocationReference ?? "",
    fields.reason_code ? { reason_code: fields.reason_code } : {},
    {
      enabled: !!paymentLocationReference && !isProvider,
    },
  );

  const {
    requestor,
    requestor_email: requestorEmail,
    request_creation_datetime: createdAt,
    invoice_location: invoiceLocation,
    cost_center: costCenter,
    reason_code_group: reasonCodeGroup,
    reason_code: reasonCode,
    custom_field1: customField1,
    custom_field2: customField2,
    custom_field3: customField3,
    custom_cost_recovery_field1: costRecoveryField1,
    custom_cost_recovery_field2: costRecoveryField2,
    cost_recovery_required: costRecoveryRequired,
    quick_request_reference: quickRequestReference,
    quick_request_number: quickRequestNumber,
    initial_requestor_name: initialRequestorName,
    initial_requestor_email: initialRequestorEmail,
  } = fields;

  const createdByValue =
    requestor && requestorEmail ? (
      <>
        {`${requestor} (${requestorEmail})`}{" "}
        {isAutomatedBooking && (
          <span
            className="font-italic"
            data-testid={SHIPMENT_DETAILS_TEST_IDS.SHIPMENT_DETAILS_ORG_BOOKED_BY_SYSTEM}
          >
            <FormattedMessage id="quote-request-detail.bookedBySystem" defaultMessage="*booked by system" />
          </span>
        )}
      </>
    ) : null;

  const initialRequestorValue = initialRequestorName
    ? `${initialRequestorName} (${initialRequestorEmail || "-"})`
    : null;

  const initialRequestNumberValue = quickRequestReference ? (
    <a target="_blank" href={`/shipment/${quickRequestReference}/quick`} rel="noreferrer">
      {quickRequestReference}
    </a>
  ) : null;

  const showInvoiceLocationInfo = Boolean(
    invoiceLocation && invoiceLocation !== organisation.paymentLocation,
  );

  const invoiceLocationInfoIcon = showInvoiceLocationInfo ? (
    <OverlayTrigger
      key="right"
      placement="right"
      overlay={
        <Tooltip>
          <strong>
            <FormattedMessage
              id="quote-request-detail.invoiceLocationTooltip"
              defaultMessage="NOTE: The invoice location will be used while generating the invoice for this shipment."
            />
          </strong>
        </Tooltip>
      }
    >
      <span
        className="shipment-details-sheet__invoice-location-info"
        tabIndex={0}
        role="button"
        aria-label="Invoice location information"
      >
        <InfoCircleIcon />
      </span>
    </OverlayTrigger>
  ) : null;

  const invoiceLocationLabel = (
    <span className="shipment-details-sheet__invoice-location-label">
      <FormattedMessage id="quote-request-detail.invoiceLocation" defaultMessage="Invoice location" />
      {invoiceLocationInfoIcon}
    </span>
  );

  const longFieldValue = organisation.longField
    ? ReactHtmlParser(replaceLinebreaks(replaceLinks(organisation.longField)))
    : null;

  return (
    <DetailsSheetSection
      testId={SHIPMENT_DETAILS_TEST_IDS.SHIPMENT_DETAILS_SECTION_ORGANISATION}
      title={
        <FormattedMessage
          id="quote-request-detail.organisationalDetails"
          defaultMessage="Organisational details"
        />
      }
    >
      <div className="rfv2-review__section-content">
        <div className="d-flex gap-4xl justify-content-between">
          <div className="rfv2-review__shipment-title" data-testid={SHIPMENT_DETAILS_TEST_IDS.SHIPMENT_DETAILS_ORG_TITLE}>
            {organisation.title || "-"}
          </div>
          <div className="d-flex gap-md mr-lg align-items-center">
            <Avatar image={organisation.shipperLogo} name={organisation.shipperName} size="md" />
            <div>
              <div
                className="rfv2-review__company-name"
                data-testid={SHIPMENT_DETAILS_TEST_IDS.SHIPMENT_DETAILS_ORG_SHIPPER}
              >
                {organisation.shipperName}
              </div>
              <div
                className="rfv2-review__payment-location"
                data-testid={SHIPMENT_DETAILS_TEST_IDS.SHIPMENT_DETAILS_ORG_PAYMENT_LOCATION}
              >
                {organisation.paymentLocation || "-"}
              </div>
            </div>
          </div>
        </div>

        <SheetField
          testId={SHIPMENT_DETAILS_TEST_IDS.SHIPMENT_DETAILS_ORG_LONG_FIELD}
          label={getLabelWithTranslationFallback("Custom longfield 01", {
            style: "capitalize",
            paymentLocation: paymentLocationReference,
            translation: (
              <FormattedMessage
                id="new-request.quote-confirmation.organisation.customLongfield1"
                defaultMessage="Custom longfield 1"
              />
            ),
          })}
          value={longFieldValue}
        />

        <div className="rfv2-review__field-grid">
          <SheetField
            testId={SHIPMENT_DETAILS_TEST_IDS.SHIPMENT_DETAILS_ORG_CREATED_BY}
            label={<FormattedMessage id="general.createdBy" defaultMessage="Created By" />}
            value={createdByValue}
          />
          <SheetField
            testId={SHIPMENT_DETAILS_TEST_IDS.SHIPMENT_DETAILS_ORG_CREATED_AT}
            label={<FormattedMessage id="general.createdDate" defaultMessage="Creation Date" />}
            value={createdAt ? formatDate(createdAt, DateTimeFormat.fullDateTimeFormat) : null}
          />
          <SheetField
            testId={SHIPMENT_DETAILS_TEST_IDS.SHIPMENT_DETAILS_ORG_QUICK_REQUEST_NUMBER}
            label={
              <FormattedMessage
                id="quick-request-detail.org.quickRequestNumber"
                defaultMessage="Quick request number"
              />
            }
            value={quickRequestNumber}
          />
          <SheetField
            testId={SHIPMENT_DETAILS_TEST_IDS.SHIPMENT_DETAILS_ORG_INITIAL_REQUESTOR}
            label={<FormattedMessage id="initialRequestor" defaultMessage="Initial Requestor" />}
            value={initialRequestorValue}
          />
          <SheetField
            testId={SHIPMENT_DETAILS_TEST_IDS.SHIPMENT_DETAILS_ORG_INITIAL_REQUEST_NUMBER}
            label={
              <FormattedMessage
                id="quote-request-detail.initialRequestNumber"
                defaultMessage="Initial Request Number"
              />
            }
            value={initialRequestNumberValue}
          />
          <SheetField
            testId={SHIPMENT_DETAILS_TEST_IDS.SHIPMENT_DETAILS_ORG_INVOICE_LOCATION}
            label={invoiceLocationLabel}
            value={invoiceLocation}
          />
          <SheetField
            testId={SHIPMENT_DETAILS_TEST_IDS.SHIPMENT_DETAILS_ORG_COST_CENTER}
            label={getLabelWithTranslationFallback("Cost Center / WBS / Project #", {
              paymentLocation: paymentLocationReference,
              translation: <FormattedMessage id="quote-request-detail.costCenter" defaultMessage="Cost center" />,
            })}
            value={costCenter}
            hideInPrint={hideInPrint}
          />
          <SheetField
            testId={SHIPMENT_DETAILS_TEST_IDS.SHIPMENT_DETAILS_ORG_REASON_CODE_GROUP}
            label={getLabelWithTranslationFallback("Reason code group", {
              style: "capitalize",
              paymentLocation: paymentLocationReference,
              translation: (
                <FormattedMessage id="quote-request-detail.reasonCodeGroup" defaultMessage="Reason code group" />
              ),
            })}
            value={reasonCodeGroup}
            hideInPrint={hideInPrint}
          />
          <SheetField
            testId={SHIPMENT_DETAILS_TEST_IDS.SHIPMENT_DETAILS_ORG_REASON_CODE}
            label={getLabelWithTranslationFallback("Reason code", {
              style: "capitalize",
              paymentLocation: paymentLocationReference,
              translation: <FormattedMessage id="quote-request-detail.reasonCode" defaultMessage="Reason code" />,
            })}
            value={reasonCode}
            hideInPrint={hideInPrint}
          />
          <SheetField
            testId={SHIPMENT_DETAILS_TEST_IDS.SHIPMENT_DETAILS_ORG_CUSTOM_FIELD_1}
            label={getLabelWithTranslationFallback("Custom field 01", {
              style: "capitalize",
              paymentLocation: paymentLocationReference,
              translation: (
                <FormattedMessage
                  id="new-request.quote-confirmation.organisation.customField1"
                  defaultMessage="Custom field 1"
                />
              ),
            })}
            value={customField1}
            hideInPrint={hideInPrint}
          />
          <SheetField
            testId={SHIPMENT_DETAILS_TEST_IDS.SHIPMENT_DETAILS_ORG_CUSTOM_FIELD_2}
            label={getLabelWithTranslationFallback("Custom field 02", {
              style: "capitalize",
              paymentLocation: paymentLocationReference,
              translation: (
                <FormattedMessage
                  id="new-request.quote-confirmation.organisation.customField2"
                  defaultMessage="Custom field 2"
                />
              ),
            })}
            value={customField2}
            hideInPrint={hideInPrint}
          />
          <SheetField
            testId={SHIPMENT_DETAILS_TEST_IDS.SHIPMENT_DETAILS_ORG_CUSTOM_FIELD_3}
            label={getLabelWithTranslationFallback("Custom field 03", {
              style: "capitalize",
              paymentLocation: paymentLocationReference,
              translation: (
                <FormattedMessage
                  id="new-request.quote-confirmation.organisation.customField3"
                  defaultMessage="Custom field 3"
                />
              ),
            })}
            value={customField3}
          />
          {costRecoverySetting?.is_cost_recovery_required && (
            <SheetField
              testId={SHIPMENT_DETAILS_TEST_IDS.SHIPMENT_DETAILS_ORG_COST_RECOVERY}
              label={<FormattedMessage id="tasks.costRecovery" defaultMessage="Cost recovery" />}
              value={
                costRecoveryRequired === "Yes" || costRecoveryRequired === "No" ? (
                  <FormattedMessage
                    id={costRecoveryRequired === "Yes" ? "general.yes" : "general.no"}
                    defaultMessage={costRecoveryRequired}
                  />
                ) : (
                  costRecoveryRequired
                )
              }
            />
          )}
          <SheetField
            testId={SHIPMENT_DETAILS_TEST_IDS.SHIPMENT_DETAILS_ORG_COST_RECOVERY_FIELD_1}
            label={
              <FormattedMessage
                id="new-request.quote-confirmation.organisation.customCostRecoveryField1"
                values={{
                  customCostRecoveryField1: getLabel(
                    "Custom cost recovery field 01",
                    "capitalize",
                    paymentLocationReference,
                  ),
                }}
                defaultMessage="Custom cost recovery field 1"
              />
            }
            value={costRecoveryField1}
          />
          <SheetField
            testId={SHIPMENT_DETAILS_TEST_IDS.SHIPMENT_DETAILS_ORG_COST_RECOVERY_FIELD_2}
            label={
              <FormattedMessage
                id="new-request.quote-confirmation.organisation.customCostRecoveryField2"
                values={{
                  customCostRecoveryField2: getLabel(
                    "Custom cost recovery field 02",
                    "capitalize",
                    paymentLocationReference,
                  ),
                }}
                defaultMessage="Custom cost recovery field 2"
              />
            }
            value={costRecoveryField2}
          />
        </div>

        <SheetField
          testId={SHIPMENT_DETAILS_TEST_IDS.SHIPMENT_DETAILS_ORG_ADDITIONAL_COMMENTS}
          label={<FormattedMessage id="general.additionalComments" defaultMessage="Additional comments" />}
          value={organisation.additionalComments}
        />
      </div>
    </DetailsSheetSection>
  );
}
